import { Link } from "react-router-dom";
import { ArrowUpRight, Flame, UtensilsCrossed, HeartHandshake } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, KineticLines, CountNumber } from "@/components/anim";
import ParallaxImage from "@/components/ParallaxImage";
import GlowCard from "@/components/GlowCard";
import { PHONE_TEL } from "@/site";

export default function ChiSiamo() {
  const { t } = useLang();
  const a = t.about;

  return (
    <div>
      {/* PAGE HERO */}
      <section className="relative overflow-hidden pb-16 pt-36 sm:pb-20 sm:pt-44" data-testid="about-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <Reveal>
            <p className="eyebrow">{a.eyebrow}</p>
          </Reveal>
          <h1 className="mt-6 font-display text-5xl italic leading-[0.98] text-white sm:text-7xl lg:text-8xl">
            <KineticLines
              mode="load"
              delay={0.15}
              lines={[a.titleA, <span className="text-gold">{a.titleB}</span>]}
            />
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-300 sm:text-lg">
              {a.intro}
            </p>
          </Reveal>
        </div>
      </section>

      {/* STORY */}
      <section className="py-16 sm:py-24" data-testid="about-story">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-10 lg:grid-cols-2 lg:gap-20">
          <div className="flex flex-col justify-center">
            {a.story.map((paragraph, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <p className="mb-6 text-sm leading-loose text-neutral-300 sm:text-base">
                  {paragraph}
                </p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="h-full">
            <figure className="relative">
              <div className="grid grid-cols-5 items-start gap-4 sm:gap-5">
                <ParallaxImage
                  src="/img/staff.webp"
                  alt="La squadra di Gunè"
                  strength={8}
                  className="relative col-span-3 h-[380px] w-full border border-gold/10 sm:h-[560px]"
                />
                <ParallaxImage
                  src="/img/busto.webp"
                  alt="Dettaglio d'interni di Gunè"
                  strength={8}
                  className="relative col-span-2 mt-10 h-[380px] w-full border border-gold/30 sm:h-[560px]"
                />
              </div>
              <figcaption className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                {a.storyImgCaption}
              </figcaption>
              <span className="absolute -left-3 -top-3 h-16 w-16 border-l border-t border-gold/50" />
            </figure>
          </Reveal>
        </div>
      </section>

      {/* CRAFT — THE PIZZAIOLO */}
      <section className="pb-8 pt-4 sm:pb-12" data-testid="about-craft">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-10 lg:grid-cols-2 lg:gap-20">
          <Reveal className="order-2 h-full lg:order-1">
            <figure className="relative">
              <ParallaxImage
                src="/img/pizzaiolo.webp"
                alt="Il pizzaiolo di Gunè sforna la pizza dal forno a legna"
                strength={8}
                imgPosition="center 22%"
                className="relative h-[420px] w-full border border-gold/10 sm:h-[560px]"
              />
              <figcaption className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                {a.craft.caption}
              </figcaption>
            </figure>
          </Reveal>
          <div className="order-1 flex flex-col justify-center lg:order-2">
            <Reveal>
              <p className="eyebrow">{a.craft.eyebrow}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 whitespace-pre-line font-display text-4xl italic leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                {a.craft.title}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-sm leading-relaxed text-neutral-300 sm:text-base">
                {a.craft.text}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PARALLAX BAND */}
      <section className="relative h-[60vh] overflow-hidden sm:h-[70vh]" data-testid="about-parallax-band">
        <ParallaxImage
          src="/img/esterno_notte.webp"
          alt="Ristorante Gunè di notte, con l'insegna illuminata"
          strength={14}
          className="absolute inset-0 h-full"
        />
        <div className="absolute inset-0 bg-ink/45" />
        <div className="absolute inset-x-0 bottom-0 z-10 mx-auto max-w-7xl px-5 pb-10 sm:px-10">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold-light">
              {a.parallaxCaption}
            </p>
          </Reveal>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-coal pb-32 pt-24 sm:pb-40 sm:pt-32" data-testid="about-philosophy">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <div className="mb-14 grid gap-8 sm:mb-20 md:grid-cols-2 md:items-end">
            <div>
              <Reveal>
                <p className="eyebrow">{a.philosophy.eyebrow}</p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-5 whitespace-pre-line font-display text-4xl italic leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                  {a.philosophy.title}
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {a.philosophy.items.map((item, i) => {
              const icons = [Flame, UtensilsCrossed, HeartHandshake];
              const Icon = icons[i % icons.length];
              return (
                <Reveal key={item.n} delay={i * 0.12} y={44} className="h-full">
                  <GlowCard
                    as="article"
                    data-testid={`philosophy-card-${i}`}
                    className="group h-full border border-gold/10 bg-ink p-8 sm:p-10"
                  >
                    <div className="flex items-start justify-between">
                      <Icon size={26} strokeWidth={1.4} className="text-gold" />
                      <span className="font-display text-3xl italic text-gold/35 transition-colors duration-500 group-hover:text-gold">
                        <CountNumber value={parseInt(item.n, 10)} />
                      </span>
                    </div>
                    <h3 className="mt-16 font-display text-2xl text-white sm:text-[1.7rem]">
                      {item.title}
                    </h3>
                    <p className="mt-4 text-sm leading-relaxed text-neutral-400">
                      {item.text}
                    </p>
                  </GlowCard>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-16 flex justify-center">
              <Link
                to="/contatti#prenota"
                data-testid="about-cta-link"
                className="group inline-flex items-center gap-3 border border-gold/50 px-9 py-4 text-[13px] uppercase tracking-[0.2em] text-gold transition-all duration-500 hover:bg-gold hover:text-black"
              >
                {a.cta}
                <ArrowUpRight size={15} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
